import { chicagoWallMs, fmtChicagoPattern, isYMD } from "./chicagoTime";
import { formatTimeRange } from "./openHouseHelpers";
import { interpolate } from "./emailPlaceholders";
import { feedbackGroups } from "./checkinCounts";
import { accessSection, forSubjectLine, withDoorCode } from "./sellerDoorCode";
import { forHostMessage, HOST_LOCKBOX_LINE, OUTSIDE_HOST_ACCESS_NOTE } from "./openHouseHostAccess";
import { accessLines, LOCKBOX_TYPES } from "./lockboxes";

// "Sat, Sep 27, 2026", always the Minnesota day: a plain YYYY-MM-DD is read as that
// calendar day, anything else (ms, Timestamp, ISO) on the Minnesota clock.
export function fmtDate(yyyyMmDd) {
  if (!yyyyMmDd) return "";
  const v = typeof yyyyMmDd === "string" ? yyyyMmDd.trim() : yyyyMmDd;
  return fmtChicagoPattern(isYMD(v) ? chicagoWallMs(v, 12, 0) : v, "ddd, MMM D, YYYY");
}

export function firstNameOf(name) {
  if (!name) return "";
  const trimmed = String(name).trim();
  return trimmed ? trimmed.split(/\s+/)[0] : "";
}

/**
 * The name, or names, this email prints for the seller's side.
 *
 * Henry, 09/25/2026, looking at an open house email for a home two people own: "Why only Bailys
 * name? I thought we fixed this???" An open house record snapshots ONE seller when it is created
 * (oh.sellerName, the first client on the deal), and every "Seller" line read that snapshot.
 *
 * So the senders now resolve the file at send time and hand the answer in:
 *   sellerOwnerNames  everyone who OWNS the home ("Bailey Lawson and Jordan Lawson"), for the
 *                     "Seller" line printed inside the hosting agent's email. It names a client
 *                     whose "Gets Emails" switch is off as well, because that switch decides who
 *                     RECEIVES email, never who owns the home the agent is standing in.
 *   sellerNames       everyone the seller email is actually addressed to (openHouseSend.js).
 *   sellerName        the one seller the record snapshotted. Still the last resort, so an open
 *                     house whose deal cannot be read reads exactly as it does today and the
 *                     "Seller" line is never printed empty.
 */
export function sellerLine(oh) {
  return String(oh?.sellerOwnerNames || oh?.sellerNames || oh?.sellerName || "").trim();
}

// Common data bag for placeholder substitution across all builders.
// `templateBody` is the body the caller is about to fill in. Seller Provided Door Code (Henry,
// 2026-09-18): when the open house carries a door code, the email sends it INSTEAD of the lockbox, so
// the lockbox values go blank and {{doorCode}} has the code. A template saved before the door code
// existed has no {{doorCode}}, so the code rides in {{lockbox}} instead (sellerDoorCode.js withDoorCode).
export function dataFor(oh, fb, templateBody) {
  // The FIRST box is what {{lockboxType}} / {{lockboxNumber}} / {{lockboxCode}} have always meant; the
  // whole list goes in {{lockbox}} (Henry, 2026-09-25: "list them all").
  const first = openHouseLockboxes(oh)[0] || null;
  const electronic = first && first.type === "Electronic";
  return {
    propertyAddress: oh?.propertyAddress || "",
    date: oh?.date ? fmtDate(oh.date) : "",
    time: (oh?.startTime && oh?.endTime) ? formatTimeRange(oh.startTime, oh.endTime) : "",
    // Whoever the senders resolved from the file, falling back to the one name the record
    // snapshotted (sellerLine, above): "Bailey Lawson and Jordan Lawson".
    sellerName: sellerLine(oh),
    // Nothing stores sellerFirstNames, so an open house on its own still reads the one name it
    // snapshotted. The seller senders hand in the joined first names: "Sara and Charlie".
    sellerFirstName: oh?.sellerFirstNames || firstNameOf(oh?.sellerName),
    hostingAgentName: oh?.hostingAgentName || "",
    hostingAgentFirstName: firstNameOf(oh?.hostingAgentName),
    ...withDoorCode(
      {
        lockboxType: first ? first.type : "",
        // Electronic lockboxes never expose the serial/code over email.
        lockboxNumber: electronic ? "" : (first && first.number) || "",
        lockboxCode: electronic ? "" : (first && first.code) || "",
        // Every box, named ("Push Button #27"), the way the vendor emails read it. An outside host's copy
        // loses the numbers and codes and gains "The listing agent will share access details before the
        // open house." (decision 10, openHouseHostAccess.forHostMessage).
        lockbox: lockboxAccessLine(oh, { withNumber: true }),
      },
      oh,
      templateBody,
    ),
    // recap-only:
    groups: fb?.groups ?? "",
    secondShowings: fb?.secondShowings ?? "",
    agentFeedback: fb?.agentFeedback || "",
    buyerFeedback: fb?.buyerFeedback || "",
    otherComments: fb?.otherComments || "",
  };
}

// The door code never goes in a subject line (spec, Henry 2026-09-18): subjects show in inbox lists
// and phone notifications. With a door code on file {{lockbox}} and the separate lockbox chips can
// carry it too, so they all go blank (sellerDoorCode.js forSubjectLine).
function subjectData(data) {
  return forSubjectLine(data);
}

// Is this the copy forHostMessage made for an OUTSIDE hosting agent? The symbol key is on that copy even
// when the listing has no lockbox at all, so its PRESENCE is the test, not its value: an outside host never
// sees a number or a code, whatever else the open house carries (decision 10, openHouseHostAccess.js).
const isOutsideHostCopy = (oh) =>
  Boolean(oh) && typeof oh === "object" && Object.prototype.hasOwnProperty.call(oh, HOST_LOCKBOX_LINE);

const str = (v) => (typeof v === "string" ? v.trim() : typeof v === "number" ? String(v) : "");

// Every lockbox this open house carries, as accessLines wants them: [{ type, number, code, where }].
// Reads the snapshot list when the open house has one (createOpenHouse and the server builder write it from
// 09/25/2026) and falls back to the single lockboxType / lockboxNumber / lockboxCode trio every open house
// booked before then still carries, so nothing has to be converted. An outside host's copy loses every
// number and code HERE, so no email can reach around the gate by reading the list instead of the trio.
export function openHouseLockboxes(oh) {
  const o = oh && typeof oh === "object" ? oh : {};
  const raw = Array.isArray(o.lockboxes) && o.lockboxes.length
    ? o.lockboxes
    : [{ type: o.lockboxType, number: o.lockboxNumber, code: o.lockboxCode }];
  const hide = isOutsideHostCopy(o);
  return raw
    .map((e) => {
      const type = str(e && e.type);
      if (!LOCKBOX_TYPES.includes(type)) return null;
      return {
        type,
        number: hide ? "" : str(e.number),
        code: hide ? "" : str(e.code),
        where: str(e.where),
      };
    })
    .filter(Boolean);
}

// The one line that describes an open house's lockboxes, or "" when it has none.
//   Push Button: includes the access code, since the agent needs it to enter.
//   Electronic:  type only. The agent gets in via the manufacturer app (Supra etc.) using their own
//                credentials; the lockbox serial number isn't actionable to share over email.
//   withNumber:  names each box ("Push Button #27"): the vendor emails and {{lockbox}} do, the
//                hosting-agent Lockbox section never has.
//   Outside host: the kinds and where they are, then "The listing agent will share access details before
//                the open house." and never a code.
// The wording itself lives in utils/lockboxes.js accessLines, shared with the listing page and mirrored on
// the server, so the three can never drift (Henry, 2026-09-25).
export function lockboxAccessLine(oh, { withNumber = false } = {}) {
  const outside = isOutsideHostCopy(oh);
  const line = accessLines(openHouseLockboxes(oh), { withNumber, includeCodes: !outside });
  if (!line) return "";
  return outside ? `${line}. ${OUTSIDE_HOST_ACCESS_NOTE}` : line;
}

// Value for the Lockbox section in the hosting-agent email: every box, never a box number.
export function lockboxSectionValue(oh) {
  return lockboxAccessLine(oh);
}

// Inline lockbox value for template bodies ({{lockbox}}) and the listing page's vendor emails: every box
// with its number. Takes the boxes as a list, or the one type / number / code trio the vendor send on the
// listing page has always passed.
export function lockboxLine(entriesOrType, number, code) {
  const entries = Array.isArray(entriesOrType)
    ? entriesOrType
    : [{ type: entriesOrType, number, code }];
  return accessLines(entries, { withNumber: true });
}

// Render a deal's participants (seller/buyer contact records) into the text the
// {{clients}} placeholder expects. Each person becomes a "• Name · phone · email"
// bullet, with any missing parts dropped. The list is newline-prefixed so it
// sits on its own lines under the "Sellers:" label the template already carries
// (e.g. "Sellers: {{clients}}"); with no participants it returns "" so the label
// line stays clean. Without this, the raw array of contact objects gets
// String()-coerced into "[object Object],[object Object],…" in the
// staging/photographer vendor emails. A plain string passes through untouched,
// so preview/sample data that already formats its own client text still works.
export function formatClientsList(clients) {
  if (typeof clients === "string") return clients;
  if (!Array.isArray(clients)) return "";
  const lines = clients
    .map((c) => {
      if (!c) return "";
      return [c.fullName || c.name, c.phone, c.email]
        .map((part) => (part || "").trim())
        .filter(Boolean)
        .join(" · ");
    })
    .filter(Boolean)
    .map((line) => `• ${line}`);
  return lines.length ? `\n${lines.join("\n")}` : "";
}

// Drop "Label:" fact lines whose value is blank or just "-" (formatDate's empty
// marker), so optional fields — target list date, lockbox, an empty sellers
// list — don't show up as empty rows in vendor emails. A label line is KEPT
// when the next line is a "• " bullet: that's the seller list sitting under its
// "Sellers:" heading. Runs on the already-rendered body, so it works for the
// default templates AND any user-customized ones.
export function stripEmptyFactLines(text) {
  if (!text) return "";
  const lines = String(text).split("\n");
  const isEmptyFact = (l) => /^\s*[^:\n]+:\s*-?\s*$/.test(l || "");
  const isBullet = (l) => /^\s*•/.test(l || "");
  return lines
    .filter((line, i) => !isEmptyFact(line) || isBullet(lines[i + 1]))
    .join("\n");
}

export const DEFAULT_TEMPLATES = {
  "scheduling-seller": {
    subject: "Open house scheduled: {{propertyAddress}}",
    body:
`Hi {{sellerFirstName}},

Just a heads-up: we have an open house scheduled at your home on {{date}}, from {{time}}.

{{hostingAgentFirstName}} will be hosting. We'll follow up with a recap of the showing afterward.

If you have any questions or need to make changes, just reply to this email.

Thanks,
Team Edelstein`,
  },
  "scheduling-agent": {
    subject: "Open house: {{propertyAddress}} on {{date}}",
    body:
`Hi {{hostingAgentFirstName}},

You're scheduled to host an open house at {{propertyAddress}} on {{date}}, {{time}}.

After the open house, please share feedback at the link below.`,
  },
  "update-seller": {
    subject: "Open house update: {{propertyAddress}}",
    body:
`Hi {{sellerFirstName}},

The open house at your home has been rescheduled.

{{hostingAgentFirstName}} will be hosting.`,
  },
  "update-agent": {
    subject: "Open house rescheduled: {{propertyAddress}}",
    body:
`Hi {{hostingAgentFirstName}},

Just a heads-up: the open house at {{propertyAddress}} has been moved.

Same public feedback link as before.`,
  },
  "cancellation-seller": {
    subject: "Open house cancelled: {{propertyAddress}}",
    body:
`Hi {{sellerFirstName}},

The open house at your home, scheduled for {{date}}, {{time}}, has been cancelled.

If you'd like to reschedule, just reply to this email.`,
  },
  "cancellation-agent": {
    subject: "Open house cancelled: {{propertyAddress}}",
    body:
`Hi {{hostingAgentFirstName}},

The open house at {{propertyAddress}} on {{date}}, {{time}}, has been cancelled.`,
  },
  "recap-seller": {
    subject: "Open house recap: {{propertyAddress}}",
    intro:
`Hi {{sellerFirstName}},

Here's the recap from the open house at your home on {{date}}, {{time}}.`,
    closing:
`Let me know if you'd like to talk through any of this.

Thanks,
Team Edelstein`,
  },

  // ===== Open House hosting requests (public hosting page + seller approval; 09/16/2026) =====
  // No sign-off in these bodies: the builders append "Thanks, <first name>" and the
  // signature block. The three server-sent ones (host confirmed, seller confirmed,
  // agent declined) are copied WORD FOR WORD into functions-api/ohRequestEmails.js;
  // src/utils/__tests__/openHouseRequestTemplates.test.js pins the exact text.
  // Placeholders: {{sellerFirstNames}} = "Kayla and Jake"; {{date}} = "Sun 09/27/2026";
  // {{time}} = "12:00pm to 2:00pm"; {{agentFirstName}} = the requesting agent.
  "oh-request-seller": {
    subject: "Open house requests for {{propertyAddress}}",
    body:
`Hi {{sellerFirstNames}},

Agents have asked to hold open houses at your home. Take a look and let us know what works. You can approve or decline each one below, or reply to this email with any questions.`,
  },
  "oh-request-host-confirmed": {
    subject: "You're confirmed to host {{propertyAddress}} on {{date}}",
    body:
`Hi {{hostingAgentFirstName}},

You're confirmed to host an open house at {{propertyAddress}} on {{date}}, {{time}}.

After the open house, please share feedback at the link below.`,
  },
  "oh-request-seller-confirmed": {
    subject: "Open house confirmed: {{propertyAddress}}",
    body:
`Hi {{sellerFirstNames}},

It's on the calendar: {{date}}, {{time}}. {{hostingAgentFirstName}} will be hosting. We'll send you a recap afterward.`,
  },
  // The decline reason is NOT in the template: buildAgentDeclinedEmail (Part B) inserts
  // it after the first paragraph when the team typed one.
  "oh-request-agent-declined": {
    subject: "Open house request for {{propertyAddress}}",
    body:
`Hi {{agentFirstName}},

Thanks for offering to host on {{date}}. The sellers can't make that one work.

Feel free to request another time.`,
  },

  // ===== Open house feedback reminders (server cron; spec 2026-09-19 Part 3) =====
  // Sent to the hosting agent 1, 5, 12 and 24 hours after the open house ends while the
  // feedback is missing. Plain text, sign-off included. Copied WORD FOR WORD from
  // functions-api/openHouseFeedbackReminders.js DEFAULT_REMINDER_TEMPLATES;
  // src/utils/__tests__/ohFeedbackReminderTemplates.test.js compares the two.
  // Placeholders: {{hostFirstName}}, {{address}} (street only), {{when}} =
  // "09/21/2026, 1:00pm to 3:00pm", {{feedbackLink}}. A "Held:" line with no date is dropped.
  "oh-feedback-reminder-1": {
    subject: "How did it go at {{address}}?",
    body:
`Hi {{hostFirstName}},

Your open house just wrapped up and we need your feedback on how it went.

Open house: {{address}}
Held: {{when}}

Fill out the feedback here (no login needed, takes about two minutes):
{{feedbackLink}}

If you are on the team, you can also do it in Team Edelstein under Open Houses, Add Feedback.

Thanks,
Team Edelstein`,
  },
  "oh-feedback-reminder-5": {
    subject: "Still need your feedback for {{address}}",
    body:
`Hi {{hostFirstName}},

Quick reminder: the feedback from your open house has not come in yet.

Open house: {{address}}
Held: {{when}}

Fill out the feedback here (no login needed, takes about two minutes):
{{feedbackLink}}

If you are on the team, you can also do it in Team Edelstein under Open Houses, Add Feedback.

Thanks,
Team Edelstein`,
  },
  "oh-feedback-reminder-12": {
    subject: "Feedback still missing for {{address}}",
    body:
`Hi {{hostFirstName}},

The open house feedback is still blank and it is needed for the recap.

Open house: {{address}}
Held: {{when}}

Fill out the feedback here (no login needed, takes about two minutes):
{{feedbackLink}}

If you are on the team, you can also do it in Team Edelstein under Open Houses, Add Feedback.

Thanks,
Team Edelstein`,
  },
  "oh-feedback-reminder-24": {
    subject: "Last reminder: feedback for {{address}}",
    body:
`Hi {{hostFirstName}},

It has been a day since the open house and the feedback is still missing. This is the last reminder.

Open house: {{address}}
Held: {{when}}

Fill out the feedback here (no login needed, takes about two minutes):
{{feedbackLink}}

If you are on the team, you can also do it in Team Edelstein under Open Houses, Add Feedback.

Thanks,
Team Edelstein`,
  },

  // ===== Client Touchpoints (manual send) =====
  "birthday": {
    subject: "Happy birthday, {{contactFirstName}}!",
    body:
`Hi {{contactFirstName}},

Wishing you a very happy birthday today! Hope you have a wonderful day with the people you love.

Thinking of you,
{{senderFirstName}}
Team Edelstein`,
  },
  "home-anniversary": {
    subject: "Happy home anniversary, {{contactFirstName}}!",
    body:
`Hi {{contactFirstName}},

It's hard to believe it's been {{yearsInHome}} year(s) since you made {{propertyAddress}} your home!

If you ever want to chat about the market, refinancing, or what your home might be worth today, I'm always here.

Cheers to many more,
{{senderFirstName}}
Team Edelstein`,
  },
  "closing-day": {
    subject: "Closing day: {{propertyAddress}}",
    body:
`Hi {{contactFirstName}},

Today's the day! Congrats on closing on {{propertyAddress}}.

If anything comes up on the day of, call me directly. Otherwise, enjoy every moment of it.

Talk soon,
{{senderFirstName}}
Team Edelstein`,
  },
  "welcome": {
    subject: "Great to meet you, {{contactFirstName}}",
    body:
`Hi {{contactFirstName}},

Really enjoyed connecting! I'm your go-to for anything real estate, so whenever something comes up, just reply or give me a call.

A few things I'll send your way over the coming weeks: market snapshots for your area, listings worth a look, and a few resources you might find handy. If anything ever isn't useful, just let me know.

Talk soon,
{{senderFirstName}}
Team Edelstein`,
  },

  // ===== Marketing (manual send) =====
  "just-listed": {
    subject: "Just listed: {{propertyAddress}}",
    body:
`Hi {{contactFirstName}},

Wanted you to be the first to know: we just brought a new listing to market at {{propertyAddress}}.

If you (or anyone you know) might be a fit, hit reply and I'll send over the full details.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },
  "just-sold": {
    subject: "Just sold: {{propertyAddress}}",
    body:
`Hi {{contactFirstName}},

Quick update: we just closed on {{propertyAddress}}. Always a great feeling to hand over the keys.

If you're curious what's happening in your own neighborhood, I'd be happy to put together a quick snapshot. Just reply.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },

  // ===== Vendor Notifications (staging) =====
  // "Door Code: {{doorCode}}" sits under the Lockbox line (Seller Provided Door Code, Henry 2026-09-18).
  // Only one of the two ever has a value, and stripEmptyFactLines drops the blank one at send time.
  "staging-consult-vendor": {
    subject: "Staging consult requested: {{propertyAddress}}",
    body:
`Hi {{vendorName}},

We'd like to schedule a staging consultation for our upcoming listing at {{propertyAddress}}.

Sellers: {{clients}}
Target list date: {{targetListDate}}
Lockbox: {{lockbox}}
Door Code: {{doorCode}}

Please let me know what dates work on your end and I'll coordinate access.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },
  "staging-install-vendor": {
    subject: "Staging install confirmed: {{propertyAddress}}",
    body:
`Hi {{vendorName}},

Confirming the staging install at {{propertyAddress}} on {{installDate}}.

Sellers: {{clients}}
Target list date: {{targetListDate}}
Lockbox: {{lockbox}}
Door Code: {{doorCode}}

The home will be ready and clear for your team. Reply if anything has changed.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },
  "staging-destage-vendor": {
    subject: "Destage scheduled: {{propertyAddress}}",
    body:
`Hi {{vendorName}},

Wanted to confirm the destage for {{propertyAddress}} on {{destageDate}}.

Closing date: {{closingDate}}
Lockbox: {{lockbox}}
Door Code: {{doorCode}}

Thanks again for the great work.

{{senderFirstName}}
Team Edelstein`,
  },
  // Appraisal back on a staged listing (Henry 10/06/2026, meeting item #10): sent by Client Care
  // from the "Email Stager" button on the task "TC: Email Stager Closing Timeline".
  "staging-appraisal-vendor": {
    subject: "Closing timeline: {{propertyAddress}}",
    body:
`Hi {{vendorName}},

Good news, the appraisal is in for {{propertyAddress}}, so the sale is on track to close.

Closing date: {{closingDate}}

Could you plan the destage and furniture pickup around that date? Please reply with the day that works for your team so everything is out before closing.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },
  "photographer-vendor": {
    subject: "Photoshoot confirmed: {{propertyAddress}}",
    body:
`Hi {{vendorName}},

Confirming the photoshoot at {{propertyAddress}} on {{photoshootDate}}.

Sellers: {{clients}}
Target list date: {{targetListDate}}
Lockbox: {{lockbox}}
Door Code: {{doorCode}}

The home will be staged and ready. Reply if anything has changed.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },
  "pre-inspection-vendor": {
    subject: "Pre-listing inspection requested: {{propertyAddress}}",
    body:
`Hi {{vendorName}},

We'd like to schedule a pre-listing inspection at {{propertyAddress}}.

Sellers: {{clients}}
Target list date: {{targetListDate}}
Lockbox: {{lockbox}}
Door Code: {{doorCode}}

Inspections requested:
{{inspectionList}}

Please let me know what dates work on your end and I'll coordinate access.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },
};

export function feedbackUrlFor(oh, origin) {
  const base =
    origin ||
    (typeof window !== "undefined" && window.location && window.location.origin) ||
    "";
  const params = new URLSearchParams({
    id: oh.id,
    t: oh.feedbackToken || "",
  });
  if (oh.propertyAddress) params.set("a", oh.propertyAddress);
  if (oh.date) params.set("d", oh.date);
  if (oh.startTime) params.set("s", oh.startTime);
  if (oh.endTime) params.set("e", oh.endTime);
  // Group count from the check-in app. Links generated after the open house
  // (recap emails, in-app copy) prefill the feedback form's groups field;
  // links generated before it have no count yet and omit the param.
  const g = feedbackGroups(oh);
  if (g != null) params.set("g", String(g));
  // HashRouter: route lives behind `#`, otherwise login redirect eats the path
  return `${base}/#/feedback?${params.toString()}`;
}

export function buildSchedulingSellerEmail(oh, overrides = {}) {
  const d = DEFAULT_TEMPLATES["scheduling-seller"];
  const bodyTpl = overrides.body ?? d.body;
  const data = dataFor(oh, undefined, bodyTpl);
  return {
    subject: interpolate(overrides.subject ?? d.subject, subjectData(data)),
    body:    interpolate(bodyTpl, data),
  };
}

export function buildSchedulingAgentEmail(oh, origin, overrides = {}) {
  // Decision 10: an outside host never sees the lockbox code or number (openHouseHostAccess.js).
  oh = forHostMessage(oh);
  const d = DEFAULT_TEMPLATES["scheduling-agent"];
  const bodyTpl = overrides.body ?? d.body;
  const data = dataFor(oh, undefined, bodyTpl);
  const url = feedbackUrlFor(oh, origin);
  const userBody = interpolate(bodyTpl, data);
  // Every owner of the home, not just the one the record snapshotted (sellerLine, 09/25/2026).
  const sellers = sellerLine(oh);
  const sellerBlock = sellers ? `\n\nSeller: ${sellers}` : "";
  // "Door Code: 4417#" when the listing has one, otherwise today's "Lockbox: ..." line.
  const access = accessSection(lockboxSectionValue(oh), oh);
  const accessBlock = access ? `\n\n${access.label}: ${access.value}` : "";
  const notesBlock = (oh?.notes && oh.notes.trim()) ? `\n\nNotes: ${oh.notes.trim()}` : "";
  const linkBlock = `\n\n${url}`;
  const signOff = "\n\nThanks for covering!\nTeam Edelstein";
  return {
    subject: interpolate(overrides.subject ?? d.subject, subjectData(data)),
    body:    userBody + sellerBlock + accessBlock + notesBlock + linkBlock + signOff,
  };
}

export function buildUpdateSellerEmail(oh, oldFields, newFields, overrides = {}) {
  const d = DEFAULT_TEMPLATES["update-seller"];
  const bodyTpl = overrides.body ?? d.body;
  const data = dataFor(oh, undefined, bodyTpl);
  const oldStr = `${fmtDate(oldFields.date)}, ${formatTimeRange(oldFields.startTime, oldFields.endTime)}`;
  const newStr = `${fmtDate(newFields.date)}, ${formatTimeRange(newFields.startTime, newFields.endTime)}`;
  const userBody = interpolate(bodyTpl, data);
  const timeBlock = `\n\nPreviously: ${oldStr}\nNow: ${newStr}`;
  const signOff = "\n\nThanks,\nTeam Edelstein";
  return {
    subject: interpolate(overrides.subject ?? d.subject, subjectData(data)),
    body:    userBody + timeBlock + signOff,
  };
}

export function buildUpdateAgentEmail(oh, oldFields, newFields, origin, overrides = {}) {
  // Decision 10: an outside host never sees the lockbox code or number (openHouseHostAccess.js).
  oh = forHostMessage(oh);
  const d = DEFAULT_TEMPLATES["update-agent"];
  const bodyTpl = overrides.body ?? d.body;
  const data = dataFor(oh, undefined, bodyTpl);
  const oldStr = `${fmtDate(oldFields.date)}, ${formatTimeRange(oldFields.startTime, oldFields.endTime)}`;
  const newStr = `${fmtDate(newFields.date)}, ${formatTimeRange(newFields.startTime, newFields.endTime)}`;
  const url = feedbackUrlFor(oh, origin);
  const userBody = interpolate(bodyTpl, data);
  const timeBlock = `\n\nPreviously: ${oldStr}\nNow: ${newStr}`;
  const linkBlock = `\n\n${url}`;
  const signOff = "\n\nThanks!\nTeam Edelstein";
  return {
    subject: interpolate(overrides.subject ?? d.subject, subjectData(data)),
    body:    userBody + timeBlock + linkBlock + signOff,
  };
}

export function buildCancellationSellerEmail(oh, reason, overrides = {}) {
  const d = DEFAULT_TEMPLATES["cancellation-seller"];
  const bodyTpl = overrides.body ?? d.body;
  const data = dataFor(oh, undefined, bodyTpl);
  const userBody = interpolate(bodyTpl, data);
  const reasonBlock = (reason && reason.trim()) ? `\n\nReason: ${reason.trim()}` : "";
  const signOff = "\n\nThanks,\nTeam Edelstein";
  return {
    subject: interpolate(overrides.subject ?? d.subject, subjectData(data)),
    body:    userBody + reasonBlock + signOff,
  };
}

export function buildCancellationAgentEmail(oh, reason, overrides = {}) {
  // Decision 10: an outside host never sees the lockbox code or number (openHouseHostAccess.js).
  oh = forHostMessage(oh);
  const d = DEFAULT_TEMPLATES["cancellation-agent"];
  const bodyTpl = overrides.body ?? d.body;
  const data = dataFor(oh, undefined, bodyTpl);
  const userBody = interpolate(bodyTpl, data);
  const reasonBlock = (reason && reason.trim()) ? `\n\nReason: ${reason.trim()}` : "";
  const signOff = "\n\nThanks!\nTeam Edelstein";
  return {
    subject: interpolate(overrides.subject ?? d.subject, subjectData(data)),
    body:    userBody + reasonBlock + signOff,
  };
}

export function buildRecapSellerEmail(oh, fb, overrides = {}) {
  const d = DEFAULT_TEMPLATES["recap-seller"];
  const introTpl = overrides.intro ?? d.intro;
  const closingTpl = overrides.closing ?? d.closing;
  const data = dataFor(oh, fb, `${introTpl}\n${closingTpl}`);
  // Apply smart formatting to feedback date/time (existing behavior preserved).
  const dateStr = /^\d{4}-\d{2}-\d{2}$/.test(String(fb.date || "").trim())
    ? fmtDate(fb.date)
    : (fb.date || fmtDate(oh.date));
  const timeMatch = String(fb.time || "").trim().match(/^(\d{1,2}):(\d{2})\s*[–-]\s*(\d{1,2}):(\d{2})$/);
  const timeStr = timeMatch
    ? formatTimeRange(`${timeMatch[1]}:${timeMatch[2]}`, `${timeMatch[3]}:${timeMatch[4]}`)
    : (fb.time || formatTimeRange(oh.startTime, oh.endTime));
  data.date = dateStr;
  data.time = timeStr;

  const intro = interpolate(introTpl, data);
  const closing = interpolate(closingTpl, data);

  const middle = [
    "",
    `Hosted by: ${fb.agentName}`,
    `Groups attended: ${fb.groups}`,
    `Potential second showings: ${fb.secondShowings}`,
    "",
    "What showing agents said:",
    fb.agentFeedback,
    "",
    "What potential buyers said:",
    fb.buyerFeedback,
  ];
  if (fb.otherComments && fb.otherComments.trim()) {
    middle.push("", "Other comments:", fb.otherComments.trim());
  }
  const body = intro + middle.join("\n") + "\n\n" + closing;
  return {
    subject: interpolate(overrides.subject ?? d.subject, subjectData(data)),
    body,
  };
}

export function buildInviteResendEmail(oh, origin, overrides = {}) {
  return buildSchedulingAgentEmail(oh, origin, overrides);
}
